import type { Track } from '@/world/Track';
import { Rng } from '@/core/Rng';
import { tuning } from '@/config/tuning';

/**
 * pickups.ts — PURE item-box placement + collection state (no three.js, no DOM).
 *
 * Boxes are laid out in Mario-Kart style rows of three across the road, spaced
 * down the course (deterministic from the track seed, so a city always plays the
 * same). Rows skip the launch-ramp zone and the finish run-in. Each box is a
 * tiny state machine: present (respawn === 0) or respawning (counts down after
 * being collected).
 */

export type BoxesParams = typeof tuning.powerups.boxes;

export interface PickupSpot {
  x: number;
  y: number;
  z: number;
  /** Arc-length position down the course (for the AI's box-seeking). */
  distance: number;
  /** Signed lateral offset from the centreline. */
  lateral: number;
  /** 0 = present and collectable; > 0 = seconds until it reappears. */
  respawn: number;
}

/** The ramp window boxes must keep clear of (from the city's RampSpec). */
export interface RampWindow {
  start: number;
  length: number;
}

/** Lay out the item boxes for a track. Deterministic for a given seed. */
export function placePickups(
  track: Track,
  seed: number,
  p: BoxesParams,
  ramp?: RampWindow,
): PickupSpot[] {
  const rng = new Rng(seed ^ 0x9e3779b9); // decorrelate from the track's own rng
  const spots: PickupSpot[] = [];
  const lastRow = track.finishDistance - p.finishClear;

  let d = p.firstRow;
  while (d <= lastRow) {
    const inRamp =
      ramp !== undefined &&
      d > ramp.start - p.rampClear &&
      d < ramp.start + ramp.length + p.rampClear;
    if (!inRamp) {
      const s = track.sampleAt(d);
      for (const lat of p.laterals) {
        const lateral = lat + rng.range(-p.lateralJitter, p.lateralJitter);
        spots.push({
          x: s.x + s.rightX * lateral,
          y: s.y + p.hover,
          z: s.z + s.rightZ * lateral,
          distance: d,
          lateral,
          respawn: 0,
        });
      }
    }
    d += p.spacing + rng.range(-p.spacingJitter, p.spacingJitter);
  }
  return spots;
}

/** Drop every box row closer to the start than `minDistance` (a NEW array —
 *  the input is untouched). The onboarding demo uses it so the first ? boxes
 *  sit further down the course, leaving the player a real stretch of dribbling
 *  before the GRAB A ? BOX lesson (owner, 2026-09-16); it never touches a
 *  normal race. Order is preserved. */
export function dropPickupsBefore(spots: PickupSpot[], minDistance: number): PickupSpot[] {
  return spots.filter((s) => s.distance >= minDistance);
}

/** Tick every collected box's respawn timer. */
export function stepPickups(spots: PickupSpot[], dt: number): void {
  for (const s of spots) {
    if (s.respawn > 0) s.respawn = Math.max(0, s.respawn - dt);
  }
}

/** Reset all boxes to present (new race). */
export function resetPickups(spots: PickupSpot[]): void {
  for (const s of spots) s.respawn = 0;
}

/**
 * Try to collect a box at (x, z): returns the index of the first PRESENT box
 * within `radius` (and marks it respawning), or -1. Plain 2D distance — the
 * boxes hover at car height, so a car driving through always connects.
 */
export function tryCollect(
  spots: PickupSpot[],
  x: number,
  z: number,
  radius: number,
  respawnTime: number,
): number {
  const r2 = radius * radius;
  for (let i = 0; i < spots.length; i++) {
    const s = spots[i];
    if (s.respawn > 0) continue;
    const dx = x - s.x;
    const dz = z - s.z;
    if (dx * dx + dz * dz <= r2) {
      s.respawn = respawnTime;
      return i;
    }
  }
  return -1;
}

/**
 * The nearest PRESENT box ahead of `carDistance` (within `maxAhead`), as the gap
 * down-course + its lateral — what the AI needs to bend toward a box on its way
 * to the ball. Returns null when none is in range.
 */
export function nearestPickupAhead(
  spots: PickupSpot[],
  carDistance: number,
  maxAhead: number,
): { ahead: number; lateral: number } | null {
  let best: { ahead: number; lateral: number } | null = null;
  for (const s of spots) {
    if (s.respawn > 0) continue;
    const ahead = s.distance - carDistance;
    if (ahead < 4 || ahead > maxAhead) continue;
    if (!best || ahead < best.ahead) best = { ahead, lateral: s.lateral };
  }
  return best;
}
